import React, { Component } from 'react';
import { ScrollView, View } from 'react-native';
import { Calendar } from 'react-native-calendars';

import moment from 'moment';
import PropTypes from 'prop-types';

import {
  Icon,
  SectionHeader,
  VFButton,
  VFScreen,
  VFText,
} from '@vfgroup-oneplatform/foundation/Components';
import { LightThemeColors, withTheme } from '@vfgroup-oneplatform/foundation/Components/Themes';
import { DateCurrencyFromatter } from '@vfgroup-oneplatform/foundation/Utils';

import { getThemeImages } from '../../../Themes';
import { CustomDay } from '../../Components';

import styles from './BalanceDateFilter.styles';
import './calendarLocaleConfig';

const { dateFormats, dateTransformer } = DateCurrencyFromatter.dateCurrencyFromatter;

class BalanceDateFilter extends Component {
  constructor(props) {
    super(props);

    const { start, end } = this.props;
    this.state = {
      currentDate: moment(),
      isArrowRight: moment().isBefore(moment(props.maxDate), 'M'),
      isArrowLeft: moment().isAfter(moment(props.minDate), 'M'),
      selectedDateOne: start && moment(start),
      selectedDateTwo: start && end && moment(end),
    };
  }

  onArrowPress = (callback, arrow) => {
    const { maxDate, minDate } = this.props;

    const addedDate = arrow === 'right' ? '1' : '-1';
    const newCurrentDate = this.state.currentDate.add(addedDate, 'M');

    this.setState(
      () => ({
        currentDate: newCurrentDate,
        isArrowRight: newCurrentDate.isBefore(moment(maxDate), 'M'),
        isArrowLeft: newCurrentDate.isAfter(moment(minDate), 'M'),
      }),
      () => callback(),
    );
  };

  onDayPress = day => {
    const dateMoment = moment(day.dateString);

    if (!this.state.selectedDateOne) {
      this.setState({ selectedDateOne: dateMoment });
    } else if (!this.state.selectedDateTwo) {
      this.setState({ selectedDateTwo: dateMoment });
    } else {
      this.setState({ selectedDateOne: dateMoment, selectedDateTwo: undefined });
    }
  };

  getMarkedDates = () => {
    const { theme } = this.props;
    const { selectedDateOne, selectedDateTwo } = this.state;

    return {
      // apply today styles
      [dateTransformer(new Date(), dateFormats.calendarFormat)]: {
        customStyles: {
          container: styles.calendarTodayContainer(theme),
        },
      },
      // apply start/end styles
      [selectedDateOne?.format(dateFormats.calendarFormat)]: {
        customStyles: {
          text: styles.calendarSelectedNumber(theme),
          period: this.getStartEndObject(selectedDateOne, selectedDateTwo),
        },
      },
      // apply start/end styles
      [selectedDateTwo?.format(dateFormats.calendarFormat)]: {
        customStyles: {
          text: styles.calendarSelectedNumber(theme),
          period: this.getStartEndObject(selectedDateTwo, selectedDateOne),
        },
      },
      // apply period styles
      ...this.getMarkedPeriod(),
    };
  };

  getStartEndObject = (current, other) => {
    return {
      start: other && current?.isBefore(other),
      end: other && current?.isAfter(other),
    };
  };

  getMarkedPeriod = () => {
    const { selectedDateOne: dateOne, selectedDateTwo: dateTwo } = this.state;
    if (!dateOne || !dateTwo) {
      return {};
    }

    const customStyles = {
      container: styles.calendarSelectedPeriod(this.props.theme),
    };

    const markedPeriodDates = {};
    const { start, end } = this.getPeriodStartEnd(dateOne, dateTwo);
    const current = start.clone().add('1', 'd');

    // add all dates between start and end period to markedPeriodDates, with
    // customStyles added to each of them
    while (current.isAfter(start) && current.isBefore(end)) {
      markedPeriodDates[dateTransformer(current, dateFormats.calendarFormat)] = { customStyles };
      current.add('1', 'd');
    }

    return markedPeriodDates;
  };

  getSelectedPeriodText = (dateOne, dateTwo) => {
    if (!dateOne && !dateTwo) {
      return undefined;
    }

    // Single date selected
    if (!dateTwo || dateOne.isSame(dateTwo)) {
      return dateTransformer(dateOne, dateFormats.oneDigitDayFullDate);
    }

    const { start, end } = this.getPeriodStartEnd(dateOne, dateTwo);

    // Period in the same month
    if (start.isSame(end, 'M')) {
      return `${start.date()} - ${dateTransformer(end, dateFormats.oneDigitDayFullDate)}`;
    }

    // Period in the same year
    if (start.isSame(end, 'y')) {
      return `${dateTransformer(start, dateFormats.oneDigitDayShortDate)} - ${dateTransformer(
        end,
        dateFormats.oneDigitDayShortMonthFullDate,
      )}`;
    }

    // Period is completely different
    return `${dateTransformer(
      start,
      dateFormats.oneDigitDayShortMonthFullDate,
    )} - ${dateTransformer(end, dateFormats.oneDigitDayShortMonthFullDate)}`;
  };

  getPeriodStartEnd = (dateOne, dateTwo) => {
    const start = dateOne.isBefore(dateTwo) ? dateOne : dateTwo;
    const end = start === dateOne ? dateTwo : dateOne;

    return { start, end };
  };

  getPeriodParam = (dateOne, dateTwo) => {
    const period = {
      selectedPeriod: this.getSelectedPeriodText(dateOne, dateTwo),
      start: dateTransformer(dateOne, dateFormats.calendarFormat),
      end: undefined,
    };

    if (dateOne && dateTwo) {
      const { start, end } = this.getPeriodStartEnd(dateOne, dateTwo);
      period.start = dateTransformer(start, dateFormats.calendarFormat);
      period.end = dateTransformer(end, dateFormats.calendarFormat);
    }

    return period;
  };

  renderSectionHeader = () => {
    return (
      <SectionHeader
        theme={this.props.theme}
        style={styles.container}
        title="balance_date_filter_screen_title"
        subtitle="balance_date_filter_screen_subtitle"
      />
    );
  };

  renderCalendar = () => {
    const { theme } = this.props;
    return (
      <Calendar
        theme={{
          calendarBackground: theme.colors.cardBackgroundColor,
          ...styles.calendarMonth(theme),
          ...styles.calendarDay(theme),
          textDayStyle: styles.calendarNumbers(theme),
          todayTextColor: styles.calendarNumbers(theme).color,
          textDisabledColor: styles.calendarDisabledNumbers.color,
        }}
        minDate={this.props.minDate}
        maxDate={this.props.maxDate}
        onDayPress={this.onDayPress}
        monthFormat={'MMMM yyyy'}
        hideExtraDays={true}
        firstDay={1}
        disableAllTouchEventsForDisabledDays={true}
        dayComponent={CustomDay}
        markingType={'custom'}
        markedDates={this.getMarkedDates()}
        renderArrow={this.renderArrows}
        disableArrowLeft={!this.state.isArrowLeft}
        onPressArrowLeft={handler => this.onArrowPress(handler, 'left')}
        disableArrowRight={!this.state.isArrowRight}
        onPressArrowRight={handler => this.onArrowPress(handler, 'right')}
      />
    );
  };

  renderArrows = direction => {
    const { isArrowRight, isArrowLeft } = this.state;
    const Images = getThemeImages(this.props.theme.name);

    const shouldRenderRightArrow = direction === 'right' && isArrowRight;
    const shouldRenderLeftArrow = direction === 'left' && isArrowLeft;

    return shouldRenderRightArrow ? (
      <Icon
        type="image"
        style={styles.calendarArrowStyle(direction)}
        name={Images.ic_chevron_right_black_white}
      />
    ) : shouldRenderLeftArrow ? (
      <Icon
        type="image"
        style={styles.calendarArrowStyle(direction)}
        name={Images.ic_chevron_left_black_white}
      />
    ) : undefined;
  };

  renderSelectedPeriod = () => {
    const { selectedDateOne: dateOne, selectedDateTwo: dateTwo } = this.state;
    const selectedPeriodText = this.getSelectedPeriodText(dateOne, dateTwo);

    return (
      <View style={styles.selectedContainer(!!selectedPeriodText)}>
        <VFText style={styles.selectedTitle} i18nKey="balance_date_filter_screen_selected_period" />
        <VFText
          style={styles.selectedValue}
          localize={false}
          // `DUMMY` is needed as placeholder text space, is doesn't get
          // displayed when `selectedPeriodText` is `undefined`
          i18nKey={selectedPeriodText ?? 'DUMMY'}
        />
      </View>
    );
  };

  renderButton = () => {
    const { onViewHistory } = this.props;
    const { selectedDateOne: dateOne, selectedDateTwo: dateTwo } = this.state;

    return (
      <>
        <View style={styles.divider(this.props.theme)} />

        <VFButton
          type="primary"
          title="balance_date_filter_screen_view_history"
          disabled={!dateOne && !dateTwo}
          onPress={() => onViewHistory(this.getPeriodParam(dateOne, dateTwo))}
        />
      </>
    );
  };

  render() {
    return (
      <VFScreen
        title="balance_history_title"
        onClose={this.props.onClose}
        clearStatusBarEntries={false}
      >
        <ScrollView>
          {this.renderSectionHeader()}

          <View style={styles.calendarContainer(this.props.theme)}>
            {this.renderCalendar()}

            {this.renderSelectedPeriod()}

            {this.renderButton()}
          </View>
        </ScrollView>
      </VFScreen>
    );
  }
}

BalanceDateFilter.defaultProps = {
  theme: LightThemeColors,
};

BalanceDateFilter.propTypes = {
  onClose: PropTypes.func.isRequired,
  minDate: PropTypes.string.isRequired,
  maxDate: PropTypes.string.isRequired,
  start: PropTypes.string,
  end: PropTypes.string,
  onViewHistory: PropTypes.func.isRequired,
  theme: PropTypes.shape({
    name: PropTypes.string,
    isDark: PropTypes.bool,
    colors: PropTypes.object,
  }),
};

export default withTheme(BalanceDateFilter);
